/**
 * Report Summary context — the ONLY way a component may read a report-wide
 * statistic. Components must never recompute or hardcode these values.
 */

import { createContext, useContext, useMemo, type ReactNode } from "react";
import {
  buildReportSummary,
  EMPTY_REPORT_SUMMARY,
  type ReportSummary,
  type ReportSummaryInputs,
} from "@/lib/report/report-summary";

const ReportSummaryContext = createContext<ReportSummary>(EMPTY_REPORT_SUMMARY);

export function ReportSummaryProvider({
  inputs,
  summary: prebuilt,
  children,
}: {
  inputs: ReportSummaryInputs;
  /**
   * PHASE 6.4 — REPAIR 1. When the page already built the summary (and its
   * canonical report) it hands that exact instance over, so the report object
   * is built exactly once per render instead of once here and once upstream.
   */
  summary?: ReportSummary;
  children: ReactNode;
}) {
  const built = useMemo(
    () => (prebuilt ? prebuilt : buildReportSummary(inputs)),
    // Rebuild when any canonical input identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      prebuilt,
      inputs.review?.id,
      inputs.review?.updated_at,
      inputs.invoiceData?.id,
      inputs.findings?.id,
      inputs.findingItems?.length,
      inputs.legalResearch?.length,
      inputs.marketComparisons?.length,
      inputs.marketResearch?.length,
      inputs.marketResearchSources?.length,
    ],
  );
  return (
    <ReportSummaryContext.Provider value={built}>{children}</ReportSummaryContext.Provider>
  );
}

/** Canonical report-wide statistics. Read-only, immutable. */
export function useReportSummary(): ReportSummary {
  return useContext(ReportSummaryContext);
}

/**
 * Tags a rendered node with the canonical field it displays so the Global
 * Consistency Certification can verify it.
 *
 *   <span {...summaryField("potentialSavings", summary.financials.potentialSavings)}>
 *     {currency(summary.financials.potentialSavings)}
 *   </span>
 */
export function summaryField(field: string, value: number | string | null) {
  return {
    "data-summary-field": field,
    "data-summary-value": value === null ? "" : String(value),
  } as const;
}

/** Marks a subtree as belonging to a section, for consistency reporting. */
export function summarySection(section: string, page = "report") {
  return {
    "data-summary-section": section,
    "data-summary-page": page,
  } as const;
}

/**
 * Tags a CHAPTER-SCOPED metric — a value that is only meaningful inside one
 * chapter (benchmark subtotals, ZIP-specific counts, chapter evidence counts).
 *
 * Chapter-scoped metrics must NEVER be promoted into the Report Summary, and
 * must never be tagged with `summaryField()`. `data-coverage-ignore` keeps the
 * untagged-currency scanner from treating them as local duplicate
 * calculations of a canonical report-wide value.
 */
export function chapterMetric(chapter: string, metric: string, value: number | string | null) {
  return {
    "data-chapter-metric": metric,
    "data-chapter-scope": chapter,
    "data-chapter-metric-value": value === null ? "" : String(value),
    "data-coverage-ignore": "chapter-scoped",
  } as const;
}
